import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "./icons";

const PULL_THRESHOLD_PX = 70;
const MAX_PULL_PX = 110;
const RESISTANCE = 0.45;

// Native-app-style pull-to-refresh for touch devices: dragging down while
// already at the top of the page reveals a spinner that fires a full page
// reload once the drag passes the threshold. Only touch pointers trigger
// it — mouse/trackpad users have no equivalent gesture, and the listeners
// stay inert until the page is scrolled to the very top.
export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (refreshing) return;

    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      startYRef.current = e.touches[0].clientY;
      draggingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!draggingRef.current || startYRef.current === null) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0 || window.scrollY > 0) {
        draggingRef.current = false;
        startYRef.current = null;
        setPull(0);
        return;
      }
      setPull(Math.min(delta * RESISTANCE, MAX_PULL_PX));
    };

    const onTouchEnd = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      startYRef.current = null;
      setPull((current) => {
        if (current >= PULL_THRESHOLD_PX) {
          setRefreshing(true);
          window.location.reload();
        }
        return current >= PULL_THRESHOLD_PX ? current : 0;
      });
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [refreshing]);

  const active = pull > 0 || refreshing;
  const progress = Math.min(pull / PULL_THRESHOLD_PX, 1);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center overflow-hidden transition-[height] duration-200 ease-engineered"
      style={{ height: active ? Math.max(pull, refreshing ? 56 : 0) : 0 }}
    >
      <div
        className="flex h-14 w-14 items-center justify-center text-accent"
        style={{
          transform: `translateY(${Math.max(pull - 40, -40)}px)`,
          opacity: progress,
        }}
      >
        <RefreshCw size={22} strokeWidth={1.6} className={refreshing ? "animate-spin" : ""} />
      </div>
    </div>
  );
}
